import { useEffect, useState } from "react"
import { getAdminUsersAnalyticsApi, AdminUsersAnalytics } from "../../features/analytics/api/adminAnalytics.api"
import { LineChart, HBarChart, DonutChart, ChartCard } from "./Charts"
import { ExclamationTriangleIcon } from "@heroicons/react/16/solid"

const ROLE_PIE = (d: AdminUsersAnalytics) => [
  { label: "Пользователи", value: d.by_role?.user ?? 0,   color: "#3b82f6" },
  { label: "Менторы",      value: d.by_role?.mentor ?? 0,  color: "#8b5cf6" },
  { label: "Администраторы", value: d.by_role?.admin ?? 0, color: "#f59e0b" },
]

export const UsersAnalyticsPage = () => {
  const [fromDate, setFromDate] = useState("")
  const [toDate, setToDate]     = useState("")
  const [d, setD] = useState<AdminUsersAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = () => {
    setLoading(true)
    setError(false)
    getAdminUsersAnalyticsApi({ from_date: fromDate || undefined, to_date: toDate || undefined })
      .then((res) => setD(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [fromDate, toDate]) // eslint-disable-line

  return (
    <div className="space-y-5">

      {/* Top stats + filter */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-3">
          <StatPill label="Всего"    value={d?.total ?? 0}               color="text-blue-600" />
          <StatPill label="Менторов" value={d?.by_role?.mentor ?? 0}     color="text-violet-600" />
          <StatPill label="Админов"  value={d?.by_role?.admin ?? 0}      color="text-amber-600" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 font-medium">Период:</span>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <span className="text-gray-300">—</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          {(fromDate || toDate) && (
            <button onClick={() => { setFromDate(""); setToDate("") }}
              className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded hover:bg-gray-100 transition-colors">
              Сбросить
            </button>
          )}
        </div>
      </div>

      {error ? (
        <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
          <ExclamationTriangleIcon className="w-8 h-8 text-red-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400 mb-2">Не удалось загрузить статистику</p>
          <button onClick={load} className="text-xs font-medium text-blue-600 hover:text-blue-700 underline">
            Повторить
          </button>
        </div>
      ) : loading || !d ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {[1, 2].map((i) => <div key={i} className="bg-white rounded-2xl border border-gray-100 h-52 animate-pulse" />)}
        </div>
      ) : (
        <>
          {/* Line chart */}
          <ChartCard title="Новые регистрации по дням">
            <LineChart data={d.new_by_day.map((r) => ({ label: r.date, value: r.count }))} color="#3b82f6" />
          </ChartCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Donut — roles */}
            <ChartCard title="Распределение по ролям">
              <DonutChart data={ROLE_PIE(d)} />
            </ChartCard>

            {/* HBar — cities */}
            <ChartCard title="Пользователи по городам">
              <HBarChart data={d.by_city.map((c) => ({ label: c.city, value: c.count }))} color="#8b5cf6" />
            </ChartCard>
          </div>
        </>
      )}
    </div>
  )
}

const StatPill = ({ label, value, color }: { label: string; value: number; color: string }) => (
  <div className="bg-white border border-gray-100 rounded-xl px-4 py-3">
    <p className="text-xs text-gray-400 mb-0.5">{label}</p>
    <p className={`text-xl font-bold ${color}`}>{value.toLocaleString("ru")}</p>
  </div>
)
